import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { SITE } from "../lib/constants";

const LegalPage = ({ type }) => {
  const { lang } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [type]);

  const content = pages[type]?.[lang] || pages.mentions.fr;

  return (
    <main
      data-testid={`legal-page-${type}`}
      className="bg-white pt-28 md:pt-36 pb-20"
    >
      <article className="container-page max-w-4xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-[#FF5A1F] transition"
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true"></i>
          {lang === "fr" ? "Retour à l'accueil" : "Back to home"}
        </Link>

        <div className="mt-6">
          <span className="chip">{content.eyebrow}</span>
          <h1 className="mt-4 font-display font-black text-4xl sm:text-5xl text-navy leading-[1.05]">
            {content.title}
          </h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {lang === "fr" ? "Dernière mise à jour : " : "Last update: "}
            {content.updated}
          </p>
        </div>

        <div className="mt-10 space-y-8 text-foreground/85 leading-relaxed">
          {content.sections.map((s, i) => (
            <section key={i} data-testid={`legal-section-${i}`}>
              <h2 className="font-display text-xl md:text-2xl font-bold text-navy">
                {s.heading}
              </h2>
              <div className="mt-3 text-sm md:text-base whitespace-pre-line">{s.body}</div>
              {s.list && (
                <ul className="mt-3 list-disc pl-6 space-y-1.5 text-sm md:text-base">
                  {s.list.map((li, j) => (
                    <li key={j}>{li}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <div className="mt-14 pt-8 border-t border-border flex flex-wrap gap-3 text-xs">
          <Link to="/mentions-legales" className="link-underline">
            {lang === "fr" ? "Mentions légales" : "Legal notice"}
          </Link>
          <Link to="/confidentialite" className="link-underline">
            {lang === "fr" ? "Confidentialité" : "Privacy"}
          </Link>
          <Link to="/rgpd" className="link-underline">
            RGPD
          </Link>
        </div>
      </article>
    </main>
  );
};

const commonUpdated = "Décembre 2025";

const pages = {
  mentions: {
    fr: {
      eyebrow: "Informations légales",
      title: "Mentions légales",
      updated: commonUpdated,
      sections: [
        {
          heading: "1. Éditeur du site",
          body: `Le présent site www.imoteps.doctor (ci-après « le Site ») est édité par :

Dr Richard ASWAD
Chirurgien orthopédique — Membre supérieur, épaule, coude, main, microchirurgie
Clinique Juge, 118 rue Jean Mermoz, 13008 Marseille
Téléphone : ${SITE.phone}
E-mail : ${SITE.email}
N° RPPS : (à renseigner)
N° ADELI : (à renseigner)
Ordre des Médecins — Conseil départemental des Bouches-du-Rhône`,
        },
        {
          heading: "2. Directeur de la publication",
          body: "Dr Richard ASWAD, en qualité de praticien exerçant à titre libéral.",
        },
        {
          heading: "3. Hébergement du site",
          body: `Le Site est hébergé par :
Emergent / OVH / (à renseigner selon l'hébergeur final)
Adresse : (à renseigner)
Téléphone : (à renseigner)`,
        },
        {
          heading: "4. Conception & développement",
          body: "Conception graphique et développement technique : réalisé dans le respect des bonnes pratiques du Conseil National de l'Ordre des Médecins et de la charte déontologique applicable aux sites web des professionnels de santé.",
        },
        {
          heading: "5. Propriété intellectuelle",
          body: "L'ensemble des contenus présents sur le Site (textes, photographies, graphismes, logos, marques, mises en page) est la propriété exclusive du Dr Richard Aswad ou de ses ayants-droit, et est protégé par les lois françaises et internationales relatives à la propriété intellectuelle.\n\nToute reproduction, représentation, modification, publication ou adaptation, totale ou partielle, des éléments du Site, quel que soit le moyen ou le procédé utilisé, est interdite sans l'autorisation écrite préalable de l'éditeur.",
        },
        {
          heading: "6. Responsabilité",
          body: "Les informations médicales diffusées sur ce Site le sont à titre purement informatif et ne sauraient en aucun cas remplacer une consultation médicale. Seul un examen clinique réalisé par le Dr Aswad, complété le cas échéant par des examens complémentaires, permet de poser un diagnostic et d'établir un plan de traitement adapté.\n\nL'éditeur ne saurait être tenu responsable des éventuelles erreurs, omissions ou inexactitudes susceptibles d'apparaître sur le Site, ni de l'utilisation qui pourrait en être faite.",
        },
        {
          heading: "7. Liens hypertextes",
          body: "Le Site peut contenir des liens vers des sites tiers (Doctolib, sociétés savantes, publications scientifiques). L'éditeur n'exerce aucun contrôle sur ces sites et décline toute responsabilité quant à leur contenu.",
        },
        {
          heading: "8. Secret médical & déontologie",
          body: "Conformément à l'article R.4127-5 du Code de la santé publique, ce site respecte le secret médical. Aucune information nominative concernant un patient n'est diffusée sans son consentement explicite. Les témoignages et photographies publiés l'ont été avec l'accord écrit des patients concernés, dans le respect du Règlement Général sur la Protection des Données (RGPD).",
        },
        {
          heading: "9. Contact",
          body: `Pour toute question concernant ces mentions légales, vous pouvez nous contacter par courrier à l'adresse ci-dessus ou par e-mail à ${SITE.email}.`,
        },
      ],
    },
    en: {
      eyebrow: "Legal information",
      title: "Legal notice",
      updated: "December 2025",
      sections: [
        {
          heading: "1. Site publisher",
          body: `This website www.imoteps.doctor (hereinafter "the Site") is published by:

Dr Richard ASWAD
Orthopedic surgeon — Upper limb, shoulder, elbow, hand, microsurgery
Clinique Juge, 118 rue Jean Mermoz, 13008 Marseille — France
Phone: ${SITE.phone}
E-mail: ${SITE.email}
RPPS number: (to be filled)
ADELI number: (to be filled)
French Medical Council — Bouches-du-Rhône Departmental Council`,
        },
        {
          heading: "2. Publication director",
          body: "Dr Richard ASWAD, as a practitioner in private practice.",
        },
        {
          heading: "3. Hosting",
          body: `The Site is hosted by:
Emergent / OVH / (to be filled according to final host)
Address: (to be filled)
Phone: (to be filled)`,
        },
        {
          heading: "4. Design & development",
          body: "Graphic design and technical development: carried out in compliance with the best practices of the French National Council of the Order of Physicians and the ethical charter applicable to health professionals' websites.",
        },
        {
          heading: "5. Intellectual property",
          body: "All content on the Site (texts, photographs, graphics, logos, trademarks, layouts) is the exclusive property of Dr Richard Aswad or his assignees, and is protected by French and international intellectual property laws.\n\nAny reproduction, representation, modification, publication or adaptation, in whole or in part, of the elements of the Site, by any means or process, is prohibited without the prior written authorization of the publisher.",
        },
        {
          heading: "6. Liability",
          body: "Medical information on this Site is for informational purposes only and cannot replace a medical consultation. Only a clinical examination by Dr Aswad, supplemented if necessary by additional examinations, enables a diagnosis and an appropriate treatment plan.\n\nThe publisher cannot be held responsible for any errors, omissions or inaccuracies that may appear on the Site, nor for the use that may be made of them.",
        },
        {
          heading: "7. Hyperlinks",
          body: "The Site may contain links to third-party sites (Doctolib, learned societies, scientific publications). The publisher exercises no control over these sites and declines any responsibility for their content.",
        },
        {
          heading: "8. Medical secrecy & ethics",
          body: "In accordance with article R.4127-5 of the French Public Health Code, this site respects medical secrecy. No personal information concerning a patient is disclosed without their explicit consent. Testimonials and photographs published have been with the written consent of the patients concerned, in compliance with the General Data Protection Regulation (GDPR).",
        },
        {
          heading: "9. Contact",
          body: `For any question concerning this legal notice, please contact us by mail at the address above or by e-mail at ${SITE.email}.`,
        },
      ],
    },
  },

  confidentialite: {
    fr: {
      eyebrow: "Confidentialité",
      title: "Politique de confidentialité",
      updated: commonUpdated,
      sections: [
        {
          heading: "1. Préambule",
          body: "Le Dr Richard Aswad s'engage à protéger la confidentialité des données personnelles qui lui sont confiées par les visiteurs et patients du Site. La présente politique décrit les pratiques concernant la collecte, l'utilisation et la protection de vos données.",
        },
        {
          heading: "2. Données collectées",
          body: "Les données collectées via le formulaire de contact sont strictement limitées à celles nécessaires au traitement de votre demande :",
          list: [
            "Identité : nom, prénom",
            "Coordonnées : adresse e-mail, numéro de téléphone (facultatif)",
            "Motif de la demande : consultation, traumatologie du sport, expertise, autre",
            "Contenu du message : sujet et description libre",
          ],
        },
        {
          heading: "3. Finalité du traitement",
          body: "Les données collectées sont utilisées exclusivement pour :",
          list: [
            "Répondre à vos demandes de rendez-vous ou d'information",
            "Assurer le suivi administratif de votre dossier",
            "Respecter nos obligations légales et réglementaires (dossier médical, facturation)",
          ],
        },
        {
          heading: "4. Base légale",
          body: "Le traitement repose sur votre consentement (formulaire de contact) et, le cas échéant, sur l'exécution d'une prestation médicale (consultation ou intervention).",
        },
        {
          heading: "5. Destinataires",
          body: "Vos données sont strictement confidentielles et ne sont transmises qu'au secrétariat du Dr Aswad et, le cas échéant, aux professionnels de santé impliqués dans votre prise en charge, dans le respect du secret médical. Aucune donnée n'est cédée ou vendue à des tiers.",
        },
        {
          heading: "6. Durée de conservation",
          body: "Les données issues du formulaire de contact sont conservées pour une durée maximale de 3 ans à compter du dernier contact. Les données du dossier médical sont conservées 20 ans après le dernier acte médical, conformément à la réglementation française.",
        },
        {
          heading: "7. Sécurité",
          body: "Le Dr Aswad met en œuvre toutes les mesures techniques et organisationnelles raisonnables pour garantir la sécurité et la confidentialité de vos données : chiffrement TLS sur le site, accès restreints, sauvegardes sécurisées, hébergement conforme HDS (Hébergeur de Données de Santé) pour les données médicales.",
        },
        {
          heading: "8. Cookies",
          body: "Le Site utilise uniquement des cookies techniques strictement nécessaires au fonctionnement (mémorisation du choix de langue FR/EN). Aucun cookie de tracking ou de publicité tiers n'est déposé sans votre consentement explicite.",
        },
        {
          heading: "9. Contact DPO",
          body: `Pour toute question ou pour exercer vos droits, vous pouvez contacter le Dr Aswad à l'adresse ${SITE.email} ou par courrier à : Clinique Juge, 118 rue Jean Mermoz, 13008 Marseille.`,
        },
      ],
    },
    en: {
      eyebrow: "Privacy",
      title: "Privacy policy",
      updated: "December 2025",
      sections: [
        {
          heading: "1. Preamble",
          body: "Dr Richard Aswad is committed to protecting the confidentiality of personal data entrusted to him by visitors and patients of the Site. This policy describes practices regarding the collection, use and protection of your data.",
        },
        {
          heading: "2. Data collected",
          body: "Data collected via the contact form is strictly limited to what is necessary to process your request:",
          list: [
            "Identity: surname, first name",
            "Contact: e-mail, phone (optional)",
            "Reason: consultation, sports traumatology, expertise, other",
            "Message: subject and free description",
          ],
        },
        {
          heading: "3. Purpose",
          body: "Collected data is used exclusively to:",
          list: [
            "Reply to your appointment or information requests",
            "Ensure administrative follow-up of your file",
            "Comply with legal and regulatory obligations (medical record, billing)",
          ],
        },
        {
          heading: "4. Legal basis",
          body: "Processing is based on your consent (contact form) and, where applicable, on the performance of a medical service (consultation or surgery).",
        },
        {
          heading: "5. Recipients",
          body: "Your data is strictly confidential and only transmitted to Dr Aswad's secretariat and, where applicable, to healthcare professionals involved in your care, in compliance with medical secrecy. No data is transferred or sold to third parties.",
        },
        {
          heading: "6. Retention",
          body: "Contact form data is kept for a maximum of 3 years from the last contact. Medical record data is kept for 20 years after the last medical act, in accordance with French regulations.",
        },
        {
          heading: "7. Security",
          body: "Dr Aswad implements all reasonable technical and organizational measures to guarantee data security and confidentiality: TLS encryption on the site, restricted access, secure backups, HDS-compliant hosting for medical data.",
        },
        {
          heading: "8. Cookies",
          body: "The Site only uses technical cookies strictly necessary for operation (language preference FR/EN). No tracking or third-party advertising cookies are set without your explicit consent.",
        },
        {
          heading: "9. DPO contact",
          body: `For any question or to exercise your rights, contact Dr Aswad at ${SITE.email} or by mail: Clinique Juge, 118 rue Jean Mermoz, 13008 Marseille, France.`,
        },
      ],
    },
  },

  rgpd: {
    fr: {
      eyebrow: "RGPD",
      title: "Vos droits RGPD",
      updated: commonUpdated,
      sections: [
        {
          heading: "Règlement Général sur la Protection des Données",
          body: "Conformément au Règlement (UE) 2016/679 du 27 avril 2016 (RGPD) et à la loi française « Informatique et Libertés » modifiée, vous disposez de droits étendus sur vos données personnelles.",
        },
        {
          heading: "Vos droits",
          body: "Vous pouvez à tout moment exercer les droits suivants :",
          list: [
            "Droit d'accès : obtenir la copie des données que nous détenons vous concernant",
            "Droit de rectification : corriger des données inexactes ou incomplètes",
            "Droit à l'effacement : demander la suppression de vos données, sous réserve des obligations légales de conservation (dossier médical)",
            "Droit d'opposition : vous opposer, pour motif légitime, au traitement de vos données",
            "Droit à la limitation : demander que le traitement soit temporairement restreint",
            "Droit à la portabilité : recevoir vos données dans un format lisible et les transmettre à un autre responsable de traitement",
            "Droit de définir des directives post-mortem relatives à la conservation et à la communication de vos données",
          ],
        },
        {
          heading: "Comment exercer vos droits ?",
          body: `Vous pouvez exercer ces droits à tout moment en nous contactant :

• Par e-mail : ${SITE.email}
• Par courrier postal : Dr Richard ASWAD — Clinique Juge, 118 rue Jean Mermoz, 13008 Marseille
• Par téléphone : ${SITE.phone}

Une pièce d'identité pourra vous être demandée afin de vérifier votre identité. La réponse à votre demande interviendra dans un délai maximum d'un mois.`,
        },
        {
          heading: "Réclamation auprès de la CNIL",
          body: "Si, après nous avoir contactés, vous estimez que vos droits n'ont pas été respectés, vous pouvez introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :\n\n3 Place de Fontenoy — TSA 80715 — 75334 PARIS CEDEX 07\nTéléphone : 01 53 73 22 22\nwww.cnil.fr",
        },
        {
          heading: "Données de santé",
          body: "Les données de santé bénéficient d'une protection renforcée. Elles sont hébergées chez un Hébergeur de Données de Santé (HDS) certifié et ne sont accessibles qu'aux professionnels de santé autorisés, dans le cadre strict de votre prise en charge.",
        },
        {
          heading: "Formulaire de contact",
          body: "Les données saisies dans le formulaire de contact sont utilisées uniquement pour répondre à votre demande. Elles ne sont ni vendues, ni transmises à des tiers à des fins commerciales.",
        },
      ],
    },
    en: {
      eyebrow: "GDPR",
      title: "Your GDPR rights",
      updated: "December 2025",
      sections: [
        {
          heading: "General Data Protection Regulation",
          body: "In accordance with Regulation (EU) 2016/679 of 27 April 2016 (GDPR) and the French \"Informatique et Libertés\" law as amended, you have extensive rights over your personal data.",
        },
        {
          heading: "Your rights",
          body: "You can exercise the following rights at any time:",
          list: [
            "Right of access: obtain a copy of the data we hold about you",
            "Right of rectification: correct inaccurate or incomplete data",
            "Right to erasure: request the deletion of your data, subject to legal retention obligations (medical record)",
            "Right to object: oppose, for legitimate reasons, the processing of your data",
            "Right to restriction: request that processing be temporarily restricted",
            "Right to portability: receive your data in a readable format and transfer it to another controller",
            "Right to define post-mortem directives regarding the retention and communication of your data",
          ],
        },
        {
          heading: "How to exercise your rights?",
          body: `You can exercise these rights at any time by contacting us:

• By e-mail: ${SITE.email}
• By post: Dr Richard ASWAD — Clinique Juge, 118 rue Jean Mermoz, 13008 Marseille, France
• By phone: ${SITE.phone}

Proof of identity may be requested to verify your identity. A response will be provided within a maximum of one month.`,
        },
        {
          heading: "Complaint to the CNIL",
          body: "If, after contacting us, you believe your rights have not been respected, you can lodge a complaint with the French Data Protection Authority (CNIL):\n\n3 Place de Fontenoy — TSA 80715 — 75334 PARIS CEDEX 07 — France\nPhone: +33 1 53 73 22 22\nwww.cnil.fr",
        },
        {
          heading: "Health data",
          body: "Health data benefits from enhanced protection. It is hosted by a certified Health Data Host (HDS) and is only accessible to authorized healthcare professionals, strictly within the framework of your care.",
        },
        {
          heading: "Contact form",
          body: "Data submitted via the contact form is used solely to respond to your request. It is neither sold nor transmitted to third parties for commercial purposes.",
        },
      ],
    },
  },
};

export default LegalPage;
